import { useState, useRef } from "react";
import { Stack, Typography, Box, TextField, Grid, Button, CircularProgress, Link as MuiLink } from "@mui/material";
import { useNavigate, Link } from "react-router";
import { HdgTextInput } from "../../components/hdg-text-input";
import { StepFormContainer } from "../../components/step-form/container";
import { isEmailValid, Pages } from "../../utils";
import { useRequestPasswordResetMutation } from "../../hooks/useRequestPasswordResetMutation";
import { useVerifyPasswordResetCodeMutation } from "../../hooks/useVerifyPasswordResetCodeMutation";
import { useCompletePasswordResetMutation } from "../../hooks/useCompletePasswordResetMutation";

export const ForgotPasswordWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1: Request Password Reset
  const [email, setEmail] = useState<string>("");
  const { handleRequestPasswordReset, loading: requestingReset } = useRequestPasswordResetMutation();

  // Step 2: Check Email (uses same email)
  const [resendCooldown, setResendCooldown] = useState(0);
  const [canResend, setCanResend] = useState(true);

  // Step 3: Enter Code
  const [codeDigits, setCodeDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const [codeError, setCodeError] = useState<string | null>(null);
  const { handleVerifyPasswordResetCode, loading: verifyingCode } = useVerifyPasswordResetCodeMutation();
  const inputRefs = useRef<HTMLInputElement[]>([]);

  // Step 4: Create New Password
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const { handleCompletePasswordReset, loading: completingReset } = useCompletePasswordResetMutation();

  // Password validation checklist
  const passwordRequirements = {
    length: newPassword.length >= 8 && newPassword.length <= 30,
    letter: /[a-zA-Z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword),
  };

  const isPasswordRequirementsMet = Object.values(passwordRequirements).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  // Step 1: Request Password Reset
  const handleRequestReset = async () => {
    if (!email || !isEmailValid(email)) return;

    const result = await handleRequestPasswordReset(email);
    if (result.success) {
      setCurrentStep(2);
      // Start cooldown for resend (60 seconds)
      setResendCooldown(60);
      setCanResend(false);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  // Step 2: Resend Code
  const handleResendCode = async () => {
    if (!canResend || !email) return;
    
    const result = await handleRequestPasswordReset(email);
    if (result.success) {
      setResendCooldown(60);
      setCanResend(false);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  // Step 3: Handle Code Input
  const handleCodeChange = (index: number, value: string) => {
    const updated = [...codeDigits];
    const chars = value.replace(/[^0-9]/g, "").split("").slice(0, 6);

    if (!value) {
      updated[index] = "";
      setCodeDigits(updated);
      setCodeError(null);
      return;
    }

    if (chars.length === 1) {
      updated[index] = chars[0];
      setCodeDigits(updated);
      if (index < 5) inputRefs.current[index + 1]?.focus();
    } else if (chars.length === 6) {
      setCodeDigits(chars);
      inputRefs.current[5]?.blur();
    }
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !codeDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyCode = async () => {
    const code = codeDigits.join("");
    if (code.length !== 6) return;

    setCodeError(null);
    const result = await handleVerifyPasswordResetCode(email, code);
    
    if (result.success) {
      setCurrentStep(4);
    } else {
      setCodeError(result.error || "Invalid or expired code. Please try again.");
      // Clear code on error
      setCodeDigits(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    }
  };

  // Step 4: Create New Password
  const handleCreatePassword = async () => {
    if (!isPasswordRequirementsMet || !passwordsMatch) return;

    setPasswordError(null);
    const code = codeDigits.join("");
    const result = await handleCompletePasswordReset(email, code, newPassword);
    
    if (result.success) {
      setCurrentStep(5);
    } else {
      setPasswordError(result.error || "Failed to reset password. Please try again.");
    }
  };

  const handleBack = () => {
    if (currentStep === 1) {
      navigate(Pages.LOGIN);
    } else {
      setCurrentStep(currentStep - 1);
      setCodeError(null);
      setPasswordError(null);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Step 1: Request Password Reset */}
      {currentStep === 1 && (
        <StepFormContainer
          step={1}
          visible={currentStep === 1}
          isValid={!!(email && isEmailValid(email))}
          onSubmit={handleRequestReset}
          onBack={handleBack}
          buttonText={requestingReset ? "Sending..." : "Send Reset Code"}
          error={null}
        >
          <Stack spacing={3}>
            <Typography variant="h4" fontWeight="bold" className="margin-0 font-title">
              Reset Your Password
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Enter your email address and we'll send you a 6-digit code to reset your password.
            </Typography>
            <HdgTextInput
              type="email"
              label="Email"
              value={email}
              onChange={setEmail}
              disabled={requestingReset}
            />
          </Stack>
        </StepFormContainer>
      )}

      {/* Step 2: Check Email */}
      {currentStep === 2 && (
        <Stack spacing={3}>
          <Typography variant="h4" fontWeight="bold" className="margin-0 font-title">
            Check Your Email
          </Typography>
          <Typography variant="body1" color="text.secondary">
            We've sent a 6-digit password reset code to:
          </Typography>
          <Typography variant="body1" fontWeight={600} sx={{ wordBreak: "break-all" }}>
            {email}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please check your email and enter the code on the next screen. The code will expire in 15 minutes.
          </Typography>
          
          <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 4 }}>
            <Button onClick={handleBack} variant="outlined">
              Back
            </Button>
            <Button
              onClick={() => setCurrentStep(3)}
              variant="contained"
              disabled={requestingReset}
            >
              Continue
            </Button>
          </Stack>

          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Didn't receive the code?{" "}
              {canResend ? (
                <MuiLink
                  component="button"
                  onClick={handleResendCode}
                  disabled={requestingReset}
                  sx={{ fontWeight: 600 }}
                >
                  Resend
                </MuiLink>
              ) : (
                <Typography component="span" variant="body2" color="text.secondary">
                  Resend available in {resendCooldown}s
                </Typography>
              )}
            </Typography>
          </Box>
        </Stack>
      )}

      {/* Step 3: Enter Code */}
      {currentStep === 3 && (
        <Stack spacing={3}>
          <Typography variant="h4" fontWeight="bold" className="margin-0 font-title">
            Password Recovery Code
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Enter the 6-digit code we sent to {email}
          </Typography>

          <Grid container spacing={2} justifyContent="center" sx={{ mt: 2 }}>
            {codeDigits.map((digit, i) => (
              <Grid item key={i}>
                <TextField
                  inputRef={(el) => (inputRefs.current[i] = el!)}
                  value={digit || ""}
                  onChange={(e) => handleCodeChange(i, e.target.value)}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData("Text").replace(/[^0-9]/g, "").slice(0, 6);
                    if (pasted.length === 6) {
                      setCodeDigits(pasted.split(""));
                      inputRefs.current[5]?.blur();
                    }
                    e.preventDefault();
                  }}
                  onKeyDown={(e) => handleCodeKeyDown(i, e)}
                  variant="outlined"
                  error={!!codeError}
                  inputProps={{
                    inputMode: "numeric",
                    maxLength: 1,
                    style: {
                      textAlign: "center",
                      width: 40,
                      height: 40,
                      fontSize: "1.5rem",
                      fontWeight: 600,
                    },
                  }}
                />
              </Grid>
            ))}
          </Grid>

          {codeError && (
            <Typography color="error" align="center">
              {codeError}
            </Typography>
          )}

          <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 2 }}>
            <Button onClick={handleBack} variant="outlined">
              Back
            </Button>
            <Button
              onClick={handleVerifyCode}
              variant="contained"
              disabled={codeDigits.join("").length !== 6 || verifyingCode}
            >
              {verifyingCode ? <CircularProgress size={20} /> : "Verify Code"}
            </Button>
          </Stack>

          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Didn't receive the code?{" "}
              {canResend ? (
                <MuiLink
                  component="button"
                  onClick={handleResendCode}
                  disabled={requestingReset}
                  sx={{ fontWeight: 600 }}
                >
                  Resend
                </MuiLink>
              ) : (
                <Typography component="span" variant="body2" color="text.secondary">
                  Resend available in {resendCooldown}s
                </Typography>
              )}
            </Typography>
          </Box>
        </Stack>
      )}

      {/* Step 4: Create New Password */}
      {currentStep === 4 && (
        <StepFormContainer
          step={4}
          visible={currentStep === 4}
          isValid={isPasswordRequirementsMet && passwordsMatch}
          onSubmit={handleCreatePassword}
          onBack={handleBack}
          buttonText={completingReset ? "Resetting..." : "Reset Password"}
          error={passwordError}
        >
          <Stack spacing={3}>
            <Typography variant="h4" fontWeight="bold" className="margin-0 font-title">
              Create a New Password
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your new password must meet the following requirements:
            </Typography>

            <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1 }}>
              <Stack spacing={1}>
                <Typography variant="body2" color={passwordRequirements.length ? "success.main" : "error"}>
                  {passwordRequirements.length ? "✓" : "✗"} 8-30 characters
                </Typography>
                <Typography variant="body2" color={passwordRequirements.letter ? "success.main" : "error"}>
                  {passwordRequirements.letter ? "✓" : "✗"} At least one letter
                </Typography>
                <Typography variant="body2" color={passwordRequirements.number ? "success.main" : "error"}>
                  {passwordRequirements.number ? "✓" : "✗"} At least one number
                </Typography>
                <Typography variant="body2" color={passwordRequirements.special ? "success.main" : "error"}>
                  {passwordRequirements.special ? "✓" : "✗"} At least one special character
                </Typography>
              </Stack>
            </Box>

            <HdgTextInput
              type="password"
              label="New Password"
              value={newPassword}
              onChange={setNewPassword}
              disabled={completingReset}
            />

            <HdgTextInput
              type="password"
              label="Confirm New Password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              disabled={completingReset}
              validationText={confirmPassword && !passwordsMatch ? "Passwords do not match" : undefined}
              isValid={!confirmPassword || passwordsMatch}
            />
          </Stack>
        </StepFormContainer>
      )}

      {/* Step 5: Success */}
      {currentStep === 5 && (
        <Stack spacing={3} alignItems="center" sx={{ textAlign: "center" }}>
          <Typography variant="h4" fontWeight="bold" className="margin-0 font-title">
            Password Reset Complete
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your password has been successfully reset. You can now log in with your new password.
          </Typography>
          <Button
            component={Link}
            to={Pages.LOGIN}
            variant="contained"
            size="large"
            sx={{ mt: 4 }}
          >
            Log In
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Need help?{" "}
            <MuiLink component={Link} to={Pages.CONTACT_US} sx={{ fontWeight: 600 }}>
              Contact Us
            </MuiLink>
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

